/**
 * Telling the server this kiosk is alive.
 *
 * **This existed as a route with no caller, and the consequence was not a dead
 * procedure — it was a wrong answer.** `device.lastSeenAt` was written once, at
 * enrolment, and never again, so `health.ts:kioskTone` in the console marked
 * every working kiosk `bad` after ten minutes. The one surface built to answer
 * "which kiosks are alive" reported the whole fleet as dead, and both halves
 * were individually correct, which is why no test caught it.
 *
 * The beat is deliberately slower than the console's ten-minute threshold by a
 * wide margin: a kiosk that misses one because it was busy taking a payment
 * must not read as a kiosk that has gone.
 */
import { trpc } from '@/api/client'
import { depth } from '@/outbox/outbox'
import { shellInfo } from '@/shell/native'
import { useKiosk } from '@/store/kiosk'

/** Comfortably inside `KIOSK_SILENT_MS`, so one missed beat is not an alarm. */
export const BEAT_MS = 2 * 60_000

export async function beat(): Promise<void> {
  try {
    const info = await shellInfo()
    await trpc.devices.heartbeat.mutate({
      ...(info === undefined
        ? {}
        : { shellVersion: info.version, platform: info.platform, printerKind: info.printerKind }),
      // The number the console shows in red. It travels on every beat rather
      // than waiting for a diagnostic bundle, because money sitting on a
      // counter is the thing somebody needs to know about soonest.
      outboxDepth: await depth()
    })
    useKiosk.getState().setOnline(true)
  } catch {
    /*
     * Offline. The next beat carries the same facts — but the *flag* is worth
     * setting, and it was not being set here.
     *
     * `online` was written by the boot, by the catalogue load and by the
     * browser's own `online`/`offline` events, none of which fires when a
     * server stops answering a kiosk that is otherwise connected. So a shop
     * whose uplink died stayed `online: true` on screen indefinitely. This is
     * the only thing in the page that talks to the server on a timer, which
     * makes it the only thing that can notice.
     */
    useKiosk.getState().setOnline(false)
  }
}

export function startHeartbeat(): () => void {
  void beat()
  const timer = setInterval(() => void beat(), BEAT_MS)
  return () => clearInterval(timer)
}
